import * as React from 'react';
import Box from '@mui/material/Box';
import StyledDatePicker from '../components/StyledDatePicker';
import BookingInfo from '../components/BookingInfo';
import Button from '@mui/material/Button';
import AdvancedFilters from '../components/Filters/AdvancedFilters';
import FailureSnackbar from '../../../../shared/feedback/FailureSnackbar';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import { formatDate, Translate } from '../../../../shared/LanguageService';
import { ICalendarItemBasic } from '../../../calendar/Types';
import { Order, Photographer, Region } from '../BookingsCalendarTypes';
import AdvancedModeToggle from '../components/TimelineCalendar/TimelineCalendarAdvancedToggle';
import { connect } from 'react-redux';
import { getUser } from '../../../../reducers/userReducer';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import useSubmitBooking from '../state/useSubmitBooking';
import { Switch } from '@mui/material';
import Typography from '@mui/material/Typography';
import { AreaOrRegion, AreaRegionToggle } from '../components/Filters/AreaRegionToggle';

type CalendarHeadingLabels = {
    area: string;
    region: string;
    advancedMode: string;
    bookingButton: string;
    errors: { bookingFailed: string };
    hideTravelTime: string;
};

type ExternalProps = {
    user: any;
    onChangeAdvancedMode?: () => void;
    isAdvancedMode: boolean;
    selectedBookingDate: Date;
    onChangeBookingDate?: (newBookingDate: Date) => void;
    bookingInfo?: Partial<ICalendarItemBasic>;
    availableRegions: Region[];
    selectedRegion: number;
    onChangeRegionFilter?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    selectedPhotographer?: Photographer;
    selectedOrder: Order;
    selectedProperties: number[];
    handleChangeSelectedProperties: (event: number[]) => void;
    onChangeHideTravelTime?: (showTravelTime: boolean) => void;
    hideTravelTimes?: boolean;
    onAreaOrRegionChange: (value: AreaOrRegion) => void;
    shouldUseArea: boolean;
};

type CalendarHeaderProps = {
    labels: CalendarHeadingLabels;
} & ExternalProps;

const StyledGridItem = styled(Grid)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginBottom: '6px'
}));

const CalendarHeader = connect((state) => ({
    user: getUser(state)
}))(
    withRouter(
        ({
            history,
            user,
            isAdvancedMode,
            onChangeAdvancedMode,
            selectedBookingDate,
            onChangeBookingDate,
            bookingInfo,
            availableRegions,
            labels,
            selectedRegion,
            onChangeRegionFilter,
            selectedPhotographer,
            selectedOrder,
            selectedProperties,
            handleChangeSelectedProperties,
            onChangeHideTravelTime,
            hideTravelTimes,
            onAreaOrRegionChange,
            shouldUseArea
        }: CalendarHeaderProps & RouteComponentProps) => {
            let selectedDay = formatDate(new Date(selectedBookingDate), 'EEEE');

            const { handleBooking, showBookingFailureSnackbar, handleCloseBookingFailureSnackbar } =
                useSubmitBooking(
                    bookingInfo,
                    selectedBookingDate,
                    selectedOrder,
                    isAdvancedMode,
                    user,
                    history
                );

            return (
                <>
                    <StyledGridItem item>
                        <StyledDatePicker
                            user={user}
                            selectedDate={selectedBookingDate}
                            onDateChange={(newValue) => {
                                onChangeBookingDate(newValue);
                            }}
                            selectedDay={selectedDay}
                            selectedBookingDate={selectedBookingDate}
                            onChangeBookingDate={onChangeBookingDate}
                        />
                        <AdvancedModeToggle
                            isAdvancedMode={isAdvancedMode}
                            onChangeAdvancedMode={onChangeAdvancedMode}
                            advancedModeTitle={labels.advancedMode}
                        />
                        <BookingInfo
                            selectedDay={selectedDay}
                            selectedDate={selectedBookingDate}
                            photographerName={
                                selectedPhotographer != null ? selectedPhotographer.name : ''
                            }
                            startTime={bookingInfo != null ? bookingInfo.start : null}
                            endTime={bookingInfo != null ? bookingInfo.end : null}
                            showBookingInfo={bookingInfo != null}
                        />
                        <Box sx={{ marginLeft: 'auto' }}>
                            <Button
                                variant={'contained'}
                                size={'large'}
                                onClick={handleBooking}
                                disabled={!bookingInfo}
                            >
                                {labels.bookingButton}
                            </Button>
                        </Box>
                    </StyledGridItem>
                    {isAdvancedMode ? (
                        <>
                            <Box display={'flex'} alignItems={'center'} sx={{ width: '100%' }}>
                                <AreaRegionToggle
                                    labels={{ area: labels.area, region: labels.region }}
                                    isArea={shouldUseArea}
                                    onChange={onAreaOrRegionChange}
                                />
                                <AdvancedFilters
                                    shouldUseArea={shouldUseArea}
                                    regions={availableRegions}
                                    onChangeRegionFilter={onChangeRegionFilter}
                                    selectedRegion={selectedRegion}
                                    selectedProperties={selectedProperties}
                                    handleChangeSelectedProperties={handleChangeSelectedProperties}
                                />
                                <Switch
                                    checked={hideTravelTimes}
                                    onChange={onChangeHideTravelTime}
                                />
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        paddingRight: '8px',
                                        fontSize: '15px'
                                    }}
                                >
                                    {labels.hideTravelTime}
                                </Typography>
                            </Box>
                        </>
                    ) : null}
                    <FailureSnackbar
                        open={showBookingFailureSnackbar}
                        onClose={handleCloseBookingFailureSnackbar}
                        text={labels.errors.bookingFailed}
                    />
                </>
            );
        }
    )
);

const CalendarHeaderTranslationLayer = (props: ExternalProps) => {
    let labels: CalendarHeadingLabels = {
        area: Translate('orders/list/bookings-calendar/area'),
        region: Translate('orders/list/bookings-calendar/region'),
        advancedMode: Translate('orders/list/bookings-calendar/advanced-mode'),
        bookingButton: Translate('orders/list/bookings-calendar/booking-button'),
        errors: {
            bookingFailed: Translate('orders/list/bookings-calendar/errors/bookingFailed')
        },
        hideTravelTime: Translate('orders/list/bookings-calendar/hideTravelTime')
    };
    return <CalendarHeader {...props} labels={labels} />;
};

export default CalendarHeaderTranslationLayer;
